import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { MapPin, ArrowRight } from 'lucide-react'
import SEO from '../components/seo/SEO'
import Button from '../components/ui/Button'
import { PROJECTS } from '../data/projects'
import { useLocalePath } from '../hooks/useLocalePath'
import styles from './Projects.module.css'

const STATUS_COLORS: Record<string, string> = {
  Active: '#16A34A',
  Piloting: '#D97706',
  Design: '#0D7377',
}

export default function Projects() {
  const { t } = useTranslation('projects')
  const { t: tSeo } = useTranslation('seo')
  const lp = useLocalePath()

  const featured = PROJECTS[0]
  const others = PROJECTS.slice(1)

  return (
    <div className={styles.page}>
      <SEO title={tSeo('projects.title')} description={tSeo('projects.description')} canonical="/projects" />

      <section className={styles.hero}>
        <div className={styles.container}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="eyebrow">{t('hero.eyebrow')}</span>
            <h1>{t('hero.title')}</h1>
            <p className={styles.lead}>{t('hero.lead')}</p>
          </motion.div>
        </div>
      </section>

      <section className={styles.featured}>
        <div className={styles.container}>
          <motion.div
            className={styles.featuredCard}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className={styles.featuredContent}>
              <span className={styles.badge} style={{ background: STATUS_COLORS[featured.status] }}>
                {featured.status}
              </span>
              <h2>{featured.title}</h2>
              <div className={styles.location}>
                <MapPin size={16} />
                <span>{featured.location}</span>
              </div>
              <p>{featured.description}</p>
              <div className={styles.methodology}>
                <strong>Methodology:</strong> {featured.methodology}
              </div>
              <div className={styles.impactRow}>
                <div className={styles.impactItem}>
                  <span className={styles.impactValue}>{featured.impact.co2Offset.toLocaleString()}</span>
                  <span className={styles.impactLabel}>tonnes CO2</span>
                </div>
                <div className={styles.impactItem}>
                  <span className={styles.impactValue}>{featured.impact.hectares.toLocaleString()}</span>
                  <span className={styles.impactLabel}>hectares</span>
                </div>
                <div className={styles.impactItem}>
                  <span className={styles.impactValue}>{featured.impact.communities.toLocaleString()}</span>
                  <span className={styles.impactLabel}>people impacted</span>
                </div>
                <div className={styles.impactItem}>
                  <span className={styles.impactValue}>{featured.impact.biodiversitySpecies}</span>
                  <span className={styles.impactLabel}>protected species</span>
                </div>
              </div>
              <Button variant="primary" href={lp(`/projects/${featured.id}`)}>
                View Project <ArrowRight size={16} />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <section className={styles.otherProjects}>
        <div className={styles.container}>
          <h2>{t('other.title')}</h2>
          <div className={styles.projectGrid}>
            {others.map((project, i) => (
              <motion.div
                key={project.id}
                className={styles.projectCard}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <span className={styles.badge} style={{ background: STATUS_COLORS[project.status] }}>
                  {project.status}
                </span>
                <h3>{project.title}</h3>
                <div className={styles.location}>
                  <MapPin size={14} />
                  <span>{project.location}</span>
                </div>
                <p className={styles.projectDesc}>{project.description}</p>
                <div className={styles.methodology}>
                  <strong>Methodology:</strong> {project.methodology}
                </div>
                <div className={styles.impactRowSmall}>
                  <span>{project.impact.co2Offset.toLocaleString()} tCO2</span>
                  <span>{project.impact.hectares.toLocaleString()} ha</span>
                  <span>{project.impact.communities.toLocaleString()} people</span>
                </div>
                <div className={styles.servicesList}>
                  {project.services.map(s => (
                    <span key={s} className={styles.serviceTag}>{s}</span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
